import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import RestoreFromTrashRoundedIcon from "@material-ui/icons/RestoreFromTrashRounded";

import ProductService from "../../services/ProductService";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";
import PagedCardTable from "../../ui/PagedCardTable";
import { productImageSrc, onProductImageError } from "../../utils/productImage";

// what each "typePage" value means: title, icon, id key in sessionStorage,
// product filter param and how to load the parent element
const TYPES = {
  famille: {
    label: "Famille",
    icon: "fa-sitemap",
    storageKey: "idFamily",
    param: "familyId",
    load: (id) =>
      ProductService.getFamilyById(getCurrentUserId(), id).then(
        (r) => r.data["productFamily"]
      ),
  },
  subFamily: {
    label: "Sous-famille",
    icon: "fa-layer-group",
    storageKey: "idSubFamily",
    param: "subFamilyId",
    load: (id) =>
      ProductService.getProductSubFamilyById(getCurrentUserId(), id).then(
        (r) => r.data["producSubtFamily"]
      ),
  },
  category: {
    label: "Catégorie",
    icon: "fa-th-large",
    storageKey: "idCategory",
    param: "categoryId",
    load: (id) =>
      ProductService.getCategoryById(getCurrentUserId(), id).then(
        (r) => r.data["productCategory"]
      ),
  },
  brand: {
    label: "Marque",
    icon: "fa-tags",
    storageKey: "idBrand",
    param: "brandId",
    load: (id) =>
      ProductService.getBrandById(getCurrentUserId(), id).then(
        (r) => r.data["brand"]
      ),
  },
};

const ProductsByCategorieOrSubFamilyOrFamily = () => {
  const navigate = useNavigate();
  const type = TYPES[sessionStorage.getItem("typePage")];
  const parentId = type ? sessionStorage.getItem(type.storageKey) : null;

  const [parent, setParent] = useState(null);
  const [parentLoading, setParentLoading] = useState(!!type);
  const [status, setStatus] = useState("dispo");
  const [reloadKey, setReloadKey] = useState(0);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!type) return;
    type
      .load(parentId)
      .then(setParent)
      .catch(() => setParent(null))
      .finally(() => setParentLoading(false));
    // eslint-disable-next-line
  }, []);

  const fetchProducts = useCallback(
    (params) =>
      ProductService.getProductsPage(getCurrentUserId(), {
        ...params,
        isDeleted: status === "dispo" ? 0 : 1,
        [type.param]: parentId,
      }),
    // eslint-disable-next-line
    [status]
  );

  const onArchiveHandler = (id, archiveOrRestored) => {
    ProductService.archiveOrRestoreReference("Product", id, archiveOrRestored)
      .then(() => {
        setAlert({
          severity: "success",
          text:
            archiveOrRestored === 1
              ? "Produit archivé avec succès"
              : "Produit restauré avec succès",
        });
        setReloadKey((key) => key + 1);
      })
      .catch(() =>
        setAlert({ severity: "error", text: "Erreur : opération impossible" })
      );
  };

  const columns = [
    {
      label: "Image",
      width: "8%",
      align: "center",
      render: (row) => (
        <img
          alt=""
          src={productImageSrc(row.imagePath)}
          onError={onProductImageError}
          className="rounded"
          style={{
            width: 48,
            height: 48,
            objectFit: "cover",
            border: "1px solid #dee2e6",
          }}
        />
      ),
    },
    {
      label: "Libellé",
      render: (row) => (
        <div>
          <strong>{row.name}</strong>
          <div className="text-muted small">
            {[row.familyName, row.subFamilyName, row.categoryName]
              .filter(Boolean)
              .join(" › ")}
          </div>
        </div>
      ),
    },
    {
      label: "Code article",
      width: "13%",
      align: "center",
      render: (row) => row.productCode || "-",
    },
    {
      label: "Code à barre",
      width: "13%",
      align: "center",
      render: (row) => row.barcode || "-",
    },
    {
      label: "TVA",
      width: "7%",
      align: "center",
      render: (row) => (row.tva != null ? `${row.tva} %` : "-"),
    },
    {
      label: "Compléter",
      width: "12%",
      align: "center",
      render: (row) =>
        row.isComplited === 1 ? (
          <button
            className="btn btn-sm btn-outline-dark"
            onClick={() => {
              sessionStorage.setItem("idProduct", row.id);
              navigate(`/addSpecificityToProduct`);
            }}
          >
            <i className="fas fa-plus mr-1"></i> Ajouter
          </button>
        ) : (
          <button
            className="btn btn-sm btn-dark"
            onClick={() => {
              sessionStorage.setItem("idProduct", row.id);
              navigate(`/updateProduct`);
            }}
          >
            <i className="fas fa-pencil-alt mr-1"></i> Compléter
          </button>
        ),
    },
    {
      label: "Actions",
      width: "9%",
      align: "center",
      render: (row) =>
        status === "dispo" ? (
          <button
            className="btn btn-sm btn-outline-danger"
            title="Archiver"
            onClick={() => onArchiveHandler(row.id, 1)}
          >
            <i className="fas fa-trash"></i>
          </button>
        ) : (
          <button
            className="btn btn-sm btn-outline-info"
            title="Restaurer"
            onClick={() => onArchiveHandler(row.id, 0)}
          >
            <RestoreFromTrashRoundedIcon fontSize="small" />
          </button>
        ),
    },
  ];

  const statusBtnClass = (value) =>
    `btn btn-sm ${status === value ? "btn-dark" : "btn-outline-dark"}`;

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <i className="fas fa-boxes mr-2"></i>Produits
                {type ? ` par ${type.label.toLowerCase()}` : ""}
              </h4>
              <small className="text-muted">
                Consultez et gérez les produits associés
              </small>
            </div>
            <button
              className="btn btn-outline-dark mt-2 mt-sm-0"
              onClick={() => navigate(-1)}
            >
              <i className="fas fa-arrow-left mr-1"></i>
              Retour
            </button>
          </div>
        </div>
      </section>

      <div className="content">
        <div className="container-fluid">
          {!type || !parentId ? (
            <div className="alert alert-warning">
              Aucun élément sélectionné. Retournez à la liste et choisissez un
              élément.
            </div>
          ) : (
            <>
              <div
                className="card card-outline card-dark"
                style={{ borderRadius: 8, boxShadow: "0 1px 6px rgba(0,0,0,.08)" }}
              >
                <div className="card-body d-flex flex-wrap justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <span
                      className="d-inline-flex justify-content-center align-items-center bg-dark text-white rounded mr-3"
                      style={{ width: 48, height: 48 }}
                    >
                      <i className={`fas ${type.icon}`}></i>
                    </span>
                    <div>
                      <small className="text-muted text-uppercase">
                        {type.label}
                      </small>
                      {parentLoading ? (
                        <div>
                          <span className="spinner-border spinner-border-sm"></span>
                        </div>
                      ) : (
                        <h5 className="mb-0">{parent ? parent.name : "-"}</h5>
                      )}
                      {parent && (parent.sku || parent.description) && (
                        <small className="text-muted">
                          {parent.sku
                            ? `Référence : ${parent.sku}`
                            : parent.description}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="btn-group mt-2 mt-sm-0" role="group">
                    <button
                      type="button"
                      className={statusBtnClass("dispo")}
                      onClick={() => setStatus("dispo")}
                    >
                      <i className="fas fa-check-circle mr-1"></i>
                      Disponibles
                    </button>
                    <button
                      type="button"
                      className={statusBtnClass("archived")}
                      onClick={() => setStatus("archived")}
                    >
                      <i className="fas fa-archive mr-1"></i>
                      Archivés
                    </button>
                  </div>
                </div>
              </div>

              <PagedCardTable
                key={status}
                title={status === "dispo" ? "Produits" : "Produits archivés"}
                icon="fas fa-boxes"
                fetchPage={fetchProducts}
                columns={columns}
                reloadKey={reloadKey}
                searchPlaceholder="Nom, code, code à barre, SKU"
                emptyText="Aucun produit"
              />
            </>
          )}
        </div>
      </div>
      {alert && (
        <Snackbar
          open
          autoHideDuration={2500}
          onClose={() => setAlert(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert
            variant="filled"
            onClose={() => setAlert(null)}
            severity={alert.severity}
          >
            {alert.text}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};
export default ProductsByCategorieOrSubFamilyOrFamily;
